import React, {useEffect, useState} from 'react'
import { FormValues } from '../../../../hooks/use-form'
import { AccountFormState, UpdateUserInfo } from './profile-component.models'
import {auth} from '../../../../services/api/auth'
import { selectUser} from '../../../../store/auth/selectors'
import { useSelector, useDispatch } from 'react-redux'
import { setHeaderAvatar } from '../../../../store/auth/reducer'
import {baseURL} from '../../../../api'
import { useAppDialog } from '../../../../hooks/use-app-dialog'
import { AppDialogVariants } from '../../../../store/app-dialog/types'
import { useTranslation } from 'react-i18next'

type UseProfileComponentProps = {
  updateUserInfoAction: (data: UpdateUserInfo) => void
  accountFormState: AccountFormState
  setNonUpdated: () => void
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
const ZERO_FILE_SIZE = 0
const ALLOWED_EXTENSIONS = ['.png', '.jpeg', '.jpg'];

export const useProfileComponent = (props: UseProfileComponentProps) => {
  const { updateUserInfoAction, accountFormState, setNonUpdated } = props
  const [imgUrl, setImage] = useState<string>('')
  const [avatar, setAvatar] = useState<File | null>()
  const user = useSelector(selectUser)
  const dispatch = useDispatch()
  const { setAppDialog } = useAppDialog()
  const { t } = useTranslation()

  const deleteAvatar = async ()=> {
    await auth.deleteAvatar().then(()=> {
      setImage('')
      setAvatar(null)
      dispatch(setHeaderAvatar(`${baseURL}/avatar/0/small.jpg`))
    })
  }

  const getAvatar = async () => {
    await auth.getAvatarLarge(user.userId).then(
      (blob)=> {
        const url = URL.createObjectURL(blob)
        setImage(url)
      }
    ).catch(()=> {
      console.error('Avatar not found')
    })
  }

  useEffect(() => {
    getAvatar()
  }, [])

  const formValueChanges = () => {
    setNonUpdated()
  }

  const validateImage = (file: File): boolean => {
     // Check file size
     if (file.size === ZERO_FILE_SIZE ) {
      setAppDialog({
        isShow: true,
        title: t('profile.imageSizeError'),
        variant: AppDialogVariants.error,
        message: t('profile.imageZeroSizeMessage')
      })
      return false
    }
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      setAppDialog({
        isShow: true,
        title: t('profile.imageSizeError'),
        variant: AppDialogVariants.error,
        message: t('profile.imageSizeMessage', { size: '10MB' })
      })
      return false
    }

    // Check file extension
    const extension = '.' + file.name.split('.').pop()?.toLowerCase()
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      setAppDialog({
        isShow: true,
        title: t('profile.imageFormatError'),
        variant: AppDialogVariants.error,
        message: t('profile.imageFormatMessage', { extensions: ALLOWED_EXTENSIONS.join(', ') })
      })
      return false
    }

    return true
  }

  const createAvatar = (event: React.ChangeEvent<any>) => {
    setNonUpdated()
    const file = event.target.files?.[0]

    if (!file) return

    if (!validateImage(file)) {
      event.target.value = '' // Reset the file input
      return
    }

    setImage(URL.createObjectURL(file))
    setAvatar(file)
  }

  const updateUserInfoActionHandler = (data: FormValues) => {
    const payload = avatar ? {name: data.name, avatar: avatar} : {name: data.name}
    //@ts-ignore
    updateUserInfoAction(payload as UpdateUserInfo)
  }

  return {
    //methods
    formValueChanges,
    updateUserInfoActionHandler,
    accountFormState,
    createAvatar,
    deleteAvatar,

    //props
    imgUrl
  }
}
